/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import useMerchantsReviews from "../merchants/useMerchantsReviews";
import Reveal from "./Reveal";

function ReviewCard({ review }) {
  const rate = Math.max(0, Math.min(5, Number(review.rate) || 0));
  const initial = (review.name || "?").trim().charAt(0).toUpperCase();

  return (
    <article className="gl-review">
      <div className="gl-review__top">
        {review.img ? (
          <img
            className="gl-review__avatar"
            src={review.img}
            alt=""
            loading="lazy"
          />
        ) : (
          <div className="gl-review__avatar gl-review__avatar--fallback">
            {initial}
          </div>
        )}
        <div>
          <p className="gl-review__name">{review.name || "—"}</p>
          <p className="gl-review__store">{review.store || ""}</p>
        </div>
      </div>
      <div className="gl-review__stars" aria-label={`${rate} stars`}>
        {"★".repeat(rate)}
        {"☆".repeat(5 - rate)}
      </div>
      <p className="gl-review__text">{review.text}</p>
    </article>
  );
}

export default function TestimonialsSection({ isArabic }) {
  const { t } = useTranslation();
  const baseUrl = (
    process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api"
  ).replace(/\/+$/, "");

  const { reviews, loading } = useMerchantsReviews(baseUrl);

  const fallback = useMemo(() => {
    const raw = t("merchants.reviews", { returnObjects: true });
    return Array.isArray(raw)
      ? raw.map((r, i) => ({
          id: `fallback-${i}`,
          name: r.name,
          store: r.store,
          rate: r.rate,
          text: r.text,
          img: r.img,
        }))
      : [];
  }, [t]);

  const list = reviews.length > 0 ? reviews : fallback;
  const loop = list.length > 0 ? [...list, ...list] : [];

  return (
    <section className="gl-section" id="testimonials">
      <div className="gl-shell">
        <Reveal className="gl-head">
          <p className="gl-eyebrow">
            {isArabic ? "آراء العملاء" : "Testimonials"}
          </p>
          <h2 className="gl-title">
            {t("merchants.title")}
          </h2>
          <p className="gl-lead">
            {isArabic
              ? "تجارب حقيقية من تجّار يديرون أعمالهم على مدار المجرة."
              : "Real stories from merchants running their business on Madar Galaxy."}
          </p>
        </Reveal>
      </div>

      {loading && reviews.length === 0 ? (
        <div className="gl-loading">
          <div className="gl-spinner" />
        </div>
      ) : loop.length === 0 ? (
        <p className="gl-loading">
          {isArabic
            ? "لا توجد آراء متاحة حالياً"
            : "No reviews available at the moment"}
        </p>
      ) : (
        <div className="gl-marquee" dir="ltr">
          <div className="gl-marquee__track">
            {loop.map((review, i) => (
              <ReviewCard key={`${review.id}-${i}`} review={review} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
